import { useEffect, useState } from 'react';
import CampoImporto from './CampoImporto';
import CampoImpegno from './CampoImpegno';
import { etichettaImpegnoConsulente } from './impegno';
import { offerteConsulentiApi } from '../api';
import Modal from './Modal';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

const empty = {
  commessa_id: '', numero: '', descrizione: '',
  importo_max: '', ore_previste: '', unita_previste: 'giorno', data_offerta: new Date().toISOString().slice(0, 10),
  data_scadenza: '', note: '',
};

const STATO_STILE = {
  da_approvare: 'bg-blue-100 text-blue-700',
  attiva: 'bg-green-100 text-green-700',
  rifiutata: 'bg-red-100 text-red-600',
  completata: 'bg-gray-100 text-gray-600',
  chiusa: 'bg-gray-100 text-gray-600',
  annullata: 'bg-gray-100 text-gray-500',
};
const STATO_LABEL = {
  da_approvare: 'In attesa di approvazione', attiva: 'Approvata', rifiutata: 'Rifiutata',
  completata: 'Completata', chiusa: 'Chiusa', annullata: 'Annullata',
};

export default function OfferteConsulentePanel() {
  const [offerte, setOfferte] = useState([]);
  const [collegabili, setCollegabili] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => offerteConsulentiApi.list().then(setOfferte).catch(() => {});
  useEffect(() => {
    load();
    // Il consulente si collega alle COMMESSE su cui è assegnato,
    // non alle nostre offerte al cliente (che non deve vedere).
    offerteConsulentiApi.commesseCollegabili().then(setCollegabili).catch(() => setCollegabili([]));
  }, []);

  const openNew = () => { setForm(empty); setModal({ mode: 'new' }); };
  const openEdit = (r) => {
    if (r.stato !== 'da_approvare') return;
    setForm({
      ...r,
      commessa_id: r.commessa_id || '',
      importo_max: r.importo_max || '',
      ore_previste: r.ore_previste || '',
      data_offerta: r.data_offerta?.slice(0, 10) || '',
      data_scadenza: r.data_scadenza?.slice(0, 10) || '',
    });
    setModal({ mode: 'edit', id: r.id });
  };

  const save = async () => {
    if (!form.commessa_id) return alert('Seleziona la commessa di riferimento');
    if (!form.numero) return alert('Indica il numero della tua offerta');
    if (!form.descrizione) return alert('Inserisci una descrizione');
    setSaving(true);
    try {
      if (modal.mode === 'new') await offerteConsulentiApi.create(form);
      else await offerteConsulentiApi.update(modal.id, form);
      setModal(null); load();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const del = async (r) => {
    if (r.stato !== 'da_approvare') return;
    if (!confirm('Eliminare questa offerta?')) return;
    try { await offerteConsulentiApi.delete(r.id); load(); } catch (e) { alert(e); }
  };

  const f = k => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-700">Le tue offerte a Sinthema</h2>
        <button className="btn-primary btn-sm" onClick={openNew} disabled={collegabili.length === 0}>
          + Nuova offerta
        </button>
      </div>

      {collegabili.length === 0 && (
        <div className="card text-center text-gray-400 py-6 text-sm mb-3">
          Nessun lavoro ti è stato ancora assegnato. Contatta Sinthema per poter inviare le tue offerte.
        </div>
      )}

      <div className="space-y-2">
        {offerte.length === 0 && collegabili.length > 0 && (
          <div className="card text-center text-gray-400 py-6 text-sm">Nessuna offerta inviata</div>
        )}
        {offerte.map(r => (
          <div
            key={r.id}
            className={`card flex items-start gap-4 ${r.stato === 'da_approvare' ? 'cursor-pointer hover:shadow-md' : ''}`}
            onClick={() => openEdit(r)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-gray-500">{r.numero}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATO_STILE[r.stato] || 'bg-gray-100'}`}>
                  {STATO_LABEL[r.stato] || r.stato}
                </span>
              </div>
              <div className="font-medium text-gray-800 mt-1">{r.descrizione}</div>
              {/* Cliente e titolo, non solo il numero: il consulente deve
                  riconoscere il lavoro senza aprire la commessa. */}
              <div className="text-xs text-gray-500 mt-0.5">
                {(r.commessa_cliente || r.offerta_cliente) && (
                  <span className="font-medium text-gray-700">
                    {r.commessa_cliente || r.offerta_cliente}
                  </span>
                )}
                {r.commessa_numero && (
                  <span className="font-mono ml-1.5">{r.commessa_numero}</span>
                )}
                {!r.commessa_numero && !(r.commessa_cliente || r.offerta_cliente) && '—'}
              </div>
              {(r.commessa_titolo || r.offerta_titolo) && (
                <div className="text-xs text-gray-500 truncate"
                     title={r.commessa_titolo || r.offerta_titolo}>
                  {r.commessa_titolo || r.offerta_titolo}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-0.5">
                {fmt(r.importo_max)}
                {parseFloat(r.ore_previste) > 0 && ` · ${etichettaImpegnoConsulente(r.ore_previste, r.unita_previste)}`}
                {` · offerta del ${fmtDate(r.data_offerta)}`}
              </div>
              {r.note_admin && (
                <div className={`text-xs mt-1 px-2 py-1 rounded ${r.stato === 'rifiutata' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                  💬 {r.note_admin}
                </div>
              )}
            </div>
            {r.stato === 'da_approvare' && (
              <button className="btn-danger btn-sm shrink-0" onClick={e => { e.stopPropagation(); del(r); }}>✕</button>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <Modal
          title={modal.mode === 'new' ? 'Nuova offerta a Sinthema' : 'Modifica offerta'}
          onClose={() => setModal(null)}
          size="lg"
        >
          <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3">
            📄 Inserisci qui l'offerta che emetti verso Sinthema, collegandola al lavoro di riferimento.
            Sarà sottoposta ad approvazione.
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Commessa di riferimento *</label>
              <select className="input" value={form.commessa_id} onChange={e => setForm(p => ({ ...p, commessa_id: e.target.value }))}>
                <option value="">— Seleziona —</option>
                {collegabili.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.numero} — {o.titolo}{o.cliente_nome ? ` (${o.cliente_nome})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div><label className="label">Numero tua offerta *</label><input className="input" {...f('numero')} /></div>
            <div><label className="label">Data offerta</label><input className="input" type="date" {...f('data_offerta')} /></div>
            <div className="col-span-2">
              <label className="label">Descrizione attività *</label>
              <textarea className="input" rows={3} placeholder="Descrivi l'attività offerta…" {...f('descrizione')} />
            </div>
            <div><label className="label">Importo (€) *</label><CampoImporto className="input" value={form.importo_max} onChange={v => setForm(p => ({ ...p, importo_max: v }))} /></div>
            <div>
              <label className="label">Impegno previsto</label>
              <CampoImpegno ore={form.ore_previste} unita={form.unita_previste}
                            onChange={({ ore, unita }) => setForm(p => ({ ...p, ore_previste: ore, unita_previste: unita }))} />
            </div>
            <div className="col-span-2"><label className="label">Validità offerta fino al</label><input className="input" type="date" {...f('data_scadenza')} /></div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={2} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Invio…' : '📤 Invia offerta'}</button>
          </div>
        </Modal>
      )}
    </section>
  );
}
